import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type { SkillPackageCatalog } from "@/api/admin/skill-packages"

import {
  createSkillPackage,
  deleteSkillPackage,
  draftSkillPackage,
  fetchSkillPackageCatalog,
  importSkillPackage,
  publishSkillPackage,
  updateSkillPackage,
} from "@/api/admin/skill-packages"

export const SKILL_PACKAGES_KEY = ["admin", "skill-packages"] as const

export function useSkillPackageCatalogQuery(enabled = true) {
  return useQuery<SkillPackageCatalog>({
    queryKey: [...SKILL_PACKAGES_KEY, "catalog"],
    queryFn: fetchSkillPackageCatalog,
    enabled,
    staleTime: 30_000,
  })
}

export function useImportSkillPackageMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: importSkillPackage,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SKILL_PACKAGES_KEY }),
  })
}

export function usePublishSkillPackageMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ packageId, revision }: { packageId: string; revision: number }) =>
      publishSkillPackage(packageId, revision),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SKILL_PACKAGES_KEY }),
  })
}

/**
 * Create a package from the portal editor (#3074).
 *
 * The consumer `createSkillPackage` never had. Creating always lands a DRAFT —
 * publishing stays a separate, deliberate act.
 */
export function useCreateSkillPackageMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createSkillPackage,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SKILL_PACKAGES_KEY }),
  })
}

/** Save the draft again. `revision` is the loaded one — a stale one 409s. */
export function useUpdateSkillPackageMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      packageId,
      payload,
    }: {
      packageId: string
      payload: Record<string, unknown> & { revision: number }
    }) => updateSkillPackage(packageId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SKILL_PACKAGES_KEY }),
  })
}

/**
 * Ask the model for a draft (#3074).
 *
 * Deliberately NOT invalidating the catalog: nothing was written. The result is
 * unsaved form state the administrator edits and then chooses to save.
 */
export function useDraftSkillPackageMutation() {
  return useMutation({ mutationFn: draftSkillPackage })
}

export function useDeleteSkillPackageMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (packageId: string) => deleteSkillPackage(packageId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SKILL_PACKAGES_KEY }),
  })
}
